import { Color3, CreateSphere, PBRMaterial, type Mesh, type Scene } from "@babylonjs/core";
import type { FxKind } from "@tiny-seas/shared";
import { PALETTE } from "./materials.ts";

export type CombatFx = Extract<FxKind, "cannon" | "pistol" | "pistolImpact" | "ballHit" | "ballSplash" | "ballLand" | "playerHit" | "restock">;

// sRGB colours, linearised for the unlit PBR puffs.
const SMOKE = new Color3(0.55, 0.53, 0.49).toLinearSpace();
const FLASH = new Color3(1, 0.65, 0.2).toLinearSpace();
const SPLINTER = Color3.FromHexString(PALETTE.woodLight).toLinearSpace();
const WATER = Color3.FromHexString(PALETTE.water).toLinearSpace();
const DIRT = Color3.FromHexString(PALETTE.sand).toLinearSpace();
const HIT = Color3.FromHexString(PALETTE.red).toLinearSpace();
const SUPPLIES = Color3.FromHexString(PALETTE.gold).toLinearSpace();
const IMPACT = new Color3(0.9, 0.95, 1);
const DIG = new Color3(0.48, 0.33, 0.19);
const TREASURE = new Color3(1, 0.82, 0.3);
const SPLASH = new Color3(0.85, 0.95, 1);
const LEAK = new Color3(0.5, 0.8, 1);
const SWORD_HIT = new Color3(1, 0.88, 0.65);
const SWORD_BLOCK = new Color3(0.65, 0.85, 1);
const DARK_WOOD = Color3.FromHexString(PALETTE.woodDark).toLinearSpace();
const CANVAS = Color3.FromHexString(PALETTE.sail).toLinearSpace();
const ROPE_FIBRE = Color3.FromHexString(PALETTE.rope).toLinearSpace();
const IRON = Color3.FromHexString(PALETTE.iron).toLinearSpace();

interface Puff {
  mesh: Mesh;
  mat: PBRMaterial;
  age: number;
  life: number;
  vx: number;
  vy: number;
  vz: number;
  grow: number;
  /** Downward acceleration (m/s²); torn canvas drifts, splinters drop. */
  gravity: number;
}

const POOL = 96;
/** A thrown bucket's water falls from the rail (~3.4 m) before it splashes on the sea. */
const BAIL_FALL_SECONDS = 0.55;

/** Small pooled particle puffs (dirt, splash, sparkle); no per-frame allocation. */
export class Effects {
  private readonly puffs: Puff[] = [];
  private next = 0;
  /** Delayed sea splashes from thrown buckets: [x, z, seconds left], fixed slots. */
  private readonly bailSplashes = [new Float32Array(3), new Float32Array(3), new Float32Array(3), new Float32Array(3)];

  constructor(scene: Scene) {
    for (let i = 0; i < POOL; i++) {
      const mesh = CreateSphere("puff", { diameter: 1, segments: 3 }, scene);
      const mat = new PBRMaterial("puffMat", scene);
      mat.unlit = true; // Pooled bursts keep their authored colour, independent of sun/IBL.
      mat.metallic = 0;
      mat.roughness = 1;
      mesh.material = mat;
      mesh.isPickable = false;
      mesh.setEnabled(false);
      this.puffs.push({ mesh, mat, age: 0, life: 0, vx: 0, vy: 0, vz: 0, grow: 0, gravity: 9 });
    }
  }

  /** `gravity` overrides the default 9 m/s² fall; `lifeScale` stretches the 0.5–0.9 s lifetime. */
  burst(x: number, y: number, z: number, color: Color3, count: number, speed: number, size: number, emissive = 0, gravity = 9, lifeScale = 1): void {
    for (let i = 0; i < count; i++) {
      const p = this.puffs[this.next]!;
      this.next = (this.next + 1) % POOL;
      const a = Math.random() * Math.PI * 2;
      const s = speed * (0.4 + Math.random() * 0.6);
      p.vx = Math.cos(a) * s;
      p.vz = Math.sin(a) * s;
      p.vy = speed * (0.6 + Math.random());
      p.age = 0;
      p.life = (0.5 + Math.random() * 0.4) * lifeScale;
      p.grow = size;
      p.gravity = gravity;
      p.mesh.position.set(x, y, z);
      p.mesh.scaling.setAll(size * 0.4);
      p.mat.albedoColor.copyFrom(color);
      p.mat.emissiveColor.copyFrom(color).scaleInPlace(emissive);
      p.mesh.visibility = 1;
      p.mesh.setEnabled(true);
    }
  }

  /** Distinct combat bursts reuse the puff pool and cached palette colours. */
  combat(kind: CombatFx, x: number, y: number, z: number): void {
    switch (kind) {
      case "pistol":
        this.burst(x, y, z, FLASH, 3, 0.7, 0.16, 1);
        this.burst(x, y, z, SMOKE, 6, 0.8, 0.35);
        break;
      case "pistolImpact":
        this.burst(x, y, z, DIRT, 5, 1.4, 0.12);
        this.burst(x, y, z, FLASH, 3, 1.8, 0.045, 0.6);
        break;
      case "cannon":
        this.burst(x, y, z, FLASH, 5, 2, 0.55, 1);
        this.burst(x, y, z, SMOKE, 12, 2.5, 1.3);
        break;
      case "ballHit":
        this.burst(x, y, z, SPLINTER, 16, 5, 0.35);
        this.burst(x, y, z, SMOKE, 5, 2, 0.8);
        break;
      case "ballSplash":
        this.burst(x, y, z, WATER, 22, 6, 0.55, 0.15);
        break;
      case "ballLand":
        this.burst(x, y, z, DIRT, 14, 3, 0.65);
        break;
      case "playerHit":
        this.burst(x, y, z, HIT, 8, 2.5, 0.25);
        break;
      case "restock":
        this.burst(x, y, z, SUPPLIES, 10, 2, 0.25, 0.6);
        break;
    }
  }

  /** `step` is the combo cut; a landed finisher (step 2) throws a larger, hotter burst. */
  melee(result: "hit" | "blocked" | "parried", x: number, y: number, z: number, step = 0): void {
    if (result === "hit" && step === 2) {
      this.burst(x, y, z, SWORD_HIT, 16, 4.2, 0.28, 0.6);
      this.burst(x, y, z, HIT, 6, 2.2, 0.22);
      return;
    }
    this.burst(x, y, z, result === "hit" ? SWORD_HIT : SWORD_BLOCK, 8, 2.8, 0.2);
  }

  /** Shared event presentation for gameplay and the interaction lab. */
  event(kind: FxKind, x: number, y: number, z: number): void {
    switch (kind) {
      case "cannon": case "pistol": case "pistolImpact": case "ballHit": case "ballSplash": case "ballLand": case "playerHit": case "restock":
        this.combat(kind, x, y, z);
        break;
      case "impact": this.burst(x, 0.5, z, IMPACT, 10, 4, 0.8); break;
      // Hull on hull: spray at the waterline and timber splinters thrown up from the contact.
      case "ram":
        this.burst(x, 0.5, z, IMPACT, 12, 4, 0.8);
        this.burst(x, 1.5, z, SPLINTER, 20, 5, 0.4);
        break;
      case "dig": this.burst(x, y + 0.2, z, DIG, 6, 2.5, 0.35); break;
      case "treasure": case "sold": case "milestone": this.burst(x, y + 0.5, z, TREASURE, 14, 3.5, 0.3, 0.8); break;
      case "splash": this.burst(x, 0.2, z, SPLASH, 8, 3, 0.5); break;
      case "bail": {
        // Water leaves the bucket over the rail, then lands on the sea below.
        this.burst(x, y, z, WATER, 12, 1.4, 0.2, 0.15);
        const slot = this.bailSplashes.find((s) => s[2]! <= 0) ?? this.bailSplashes[0]!;
        slot[0] = x; slot[1] = z; slot[2] = BAIL_FALL_SECONDS;
        break;
      }
      case "anchorSplash":
        this.burst(x, 0.1, z, WATER, 22, 5, 0.55, 0.15);
        this.burst(x, 0.2, z, SPLASH, 10, 2.5, 0.6);
        break;
      case "leak": this.burst(x, y, z, LEAK, 8, 2, 0.3); break;
      // Sawdust and wood chips knocked off the patch as the last nail goes home.
      case "repaired":
        this.burst(x, y + 0.2, z, SPLINTER, 8, 1.1, 0.07);
        this.burst(x, y + 0.2, z, DIG, 5, 0.8, 0.1);
        break;
      case "crateHit": this.burst(x, y, z, SPLINTER, 6, 2.2, 0.14); break;
      case "crateBreak":
        this.burst(x, y, z, SPLINTER, 16, 3.5, 0.2);
        this.burst(x, y + 0.2, z, TREASURE, 8, 2.2, 0.12, 0.8);
        break;
      case "gate": this.burst(x, y - 1.8, z, DIRT, 10, 1.6, 0.5); break;
      // Bow stoves in: a heavy timber burst, sea thrown up at the waterline, and dust.
      case "ram":
        this.burst(x, y, z, SPLINTER, 26, 7, 0.45);
        this.burst(x, y, z, DARK_WOOD, 10, 5.5, 0.3);
        this.burst(x, 0.6, z, WATER, 20, 6, 0.6, 0.15);
        this.burst(x, y, z, SMOKE, 8, 2.2, 1.2);
        break;
      case "ropeSnap":
        this.burst(x, y, z, ROPE_FIBRE, 4, 0.6, 0.18, 0, 3);
        this.burst(x, y, z, ROPE_FIBRE, 6, 3.5, 0.05);
        break;
      // Canvas scraps flutter down far slower than splinters.
      case "sailTear":
        this.burst(x, y, z, CANVAS, 10, 1.6, 0.3, 0, 2, 2.2);
        this.burst(x, y, z, CANVAS, 6, 3, 0.12, 0, 2, 1.8);
        break;
      case "wheelSmash":
        this.burst(x, y, z, SPLINTER, 12, 3.8, 0.22);
        this.burst(x, y, z, DARK_WOOD, 8, 3, 0.18);
        break;
      case "gunWreck":
        this.burst(x, y, z, SPLINTER, 14, 4.5, 0.3);
        this.burst(x, y, z, IRON, 8, 3.5, 0.14);
        this.burst(x, y, z, FLASH, 6, 5, 0.05, 1);
        this.burst(x, y, z, SMOKE, 8, 2, 1);
        break;
    }
  }

  update(dt: number): void {
    for (const s of this.bailSplashes) {
      if (s[2]! <= 0) continue;
      s[2] = s[2]! - dt;
      if (s[2]! <= 0) this.burst(s[0]!, 0.2, s[1]!, SPLASH, 10, 2.6, 0.45);
    }
    for (const p of this.puffs) {
      if (p.age >= p.life) continue;
      p.age += dt;
      if (p.age >= p.life) {
        p.mesh.setEnabled(false);
        continue;
      }
      p.vy -= p.gravity * dt;
      p.mesh.position.x += p.vx * dt;
      p.mesh.position.y += p.vy * dt;
      p.mesh.position.z += p.vz * dt;
      const t = p.age / p.life;
      p.mesh.scaling.setAll(p.grow * (0.4 + t * 0.8));
      // Mesh opacity dirties only this puff, not every mesh sharing the scene.
      p.mesh.visibility = 1 - t;
    }
  }
}
